import React from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import {FormattedMessage, useIntl} from 'react-intl';
import {alpha, useTheme} from '@mui/material/styles';
import {Tooltip} from '@mui/material';
import {Button, Card, FormField, Input} from 'wobi-web-common/dist/v2/index.js';
import {destinationsToNames} from '../utils/routingHelpers';
import {ridersTypes} from '../config/ridersTypes';
import logos from '../assets/images/company-logos';
import {formatExpiryDate} from '../utils/formatters';
import {TEST_IDS} from '../config/testIds';

const CARD_NUMBER_MAX_LENGTH = 16;

const paymentFieldIconPaths = {
  id: [
    'M3 5h18a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z',
    'M9 11a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
    'M6 16a3 3 0 0 1 6 0',
    'M15 9h4',
    'M15 13h4',
  ],
  lock: ['M5 11h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2z', 'M8 11V7a4 4 0 0 1 8 0v4'],
  user: ['M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2', 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8'],
};

const PaymentFieldIcon = ({name}) => (
  <svg className='payment-field-icon' width='18' height='18' viewBox='0 0 24 24' fill='none'
    stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    {paymentFieldIconPaths[name].map(path => <path key={path} d={path}/>)}
  </svg>
);

const ChevronIcon = ({open}) => (
  <svg
    className={open ? 'payment-chevron payment-chevron--open' : 'payment-chevron'}
    width='18'
    height='18'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='m6 9 6 6 6-6'/>
  </svg>
);

const SummaryDetails = ({offer, initialTabs}) => {
  const intl = useIntl();
  const riders = ridersTypes
    .filter(({formikKey}) => initialTabs[formikKey])
    .map(({messageId}) => intl.formatMessage({id: messageId}));
  const dateRange = [initialTabs.policyStartDate, initialTabs.policyEndDate]
    .map(date => Moment(date).format('DD.MM.YY'))
    .join(' - ');
  const exchangeRate = offer.totalPriceShekel ?
    Number.parseFloat(offer.totalPriceShekel / offer.price).toFixed(2) : offer.currencyRate;

  return (
    <>
      <div className='payment-summary__logo'>
        <img src={logos[offer.logo]} alt={offer.providerName || offer.productLineName}/>
      </div>
      <dl className='payment-summary__details'>
        <div><dt><FormattedMessage id='payment.checkout.destination'/></dt><dd>{destinationsToNames(initialTabs.targetCountries)}</dd></div>
        <div><dt><FormattedMessage id='payment.checkout.dates'/></dt><dd dir='ltr'>{dateRange}</dd></div>
        <div><dt><FormattedMessage id='payment.checkout.travelers'/></dt><dd>{initialTabs.policyholdNum}</dd></div>
        <div>
          <dt><FormattedMessage id='payment.checkout.coverages'/></dt>
          <dd>{riders.length ? riders.join(', ') : intl.formatMessage({id: 'payment.checkout.basicOnly'})}</dd>
        </div>
      </dl>
      <div className='payment-summary__price-row'>
        <span>${Math.ceil(offer.price)}</span>
        <b><FormattedMessage id='payment.checkout.policyPrice'/></b>
      </div>
      <div className='payment-summary__price-row payment-summary__price-row--total'>
        <span data-testid={TEST_IDS.payment.total}>₪{offer.convertedPrice}</span>
        <b><FormattedMessage id='payment.checkout.totalDue'/></b>
      </div>
      <p className='payment-summary__rate'>
        <FormattedMessage id='payment.checkout.exchangeRate' values={{exchangeRate}}/>
      </p>
    </>
  );
};

const PaymentCheckout = ({
  errors,
  filteredCCTypes,
  handleChange,
  initialTabs,
  isSubmitting,
  offer,
  onBackToOffers,
  onCardTypeChange,
  onSubmit,
  setFieldValue,
  submitError,
  touched,
  values,
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const cardTypeSelectRef = React.useRef(null);
  const cardTypeTriggerRef = React.useRef(null);
  const [cardTypeOpen, setCardTypeOpen] = React.useState(false);
  const [summaryOpen, setSummaryOpen] = React.useState(false);
  const selectedCardType = filteredCCTypes.find(item => String(item.id) === String(values.ccType));

  React.useEffect(() => {
    const closeOnOutsideClick = event => {
      if (!cardTypeSelectRef.current?.contains(event.target)) setCardTypeOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, []);

  React.useEffect(() => {
    if (!cardTypeOpen) return undefined;
    const closeOnEscape = event => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setCardTypeOpen(false);
      cardTypeTriggerRef.current?.focus({preventScroll: true});
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [cardTypeOpen]);

  const selectCardType = value => {
    onCardTypeChange({target: {name: 'ccType', value}});
    setCardTypeOpen(false);
    cardTypeTriggerRef.current?.focus({preventScroll: true});
  };

  function fieldError(name) {
    return touched[name] ? errors[name] : undefined;
  }

  return (
    <main className='payment-page' style={{
      '--payment-primary': theme.palette.primary.main,
      '--payment-primary-dark': theme.palette.primary.dark,
      '--payment-primary-contrast': theme.palette.primary.contrastText,
      '--payment-secondary': theme.palette.secondary.main,
      '--payment-secondary-dark': theme.palette.secondary.dark,
      '--payment-text-primary': theme.palette.text.primary,
      '--payment-text-secondary': theme.palette.text.secondary,
      '--payment-background': theme.palette.background.paper,
      '--payment-menu-background': theme.palette.grey[100],
      '--payment-divider': theme.palette.divider,
      '--payment-error': theme.palette.error.main,
      '--payment-info-light': theme.palette.info.light,
      '--payment-info-dark': theme.palette.info.dark,
      '--payment-title-size': theme.typography.pxToRem(32),
      '--payment-mobile-title-size': theme.typography.pxToRem(28),
      '--payment-body-size': theme.typography.pxToRem(15),
      '--payment-input-size': theme.typography.pxToRem(16),
      '--payment-detail-size': theme.typography.pxToRem(13.5),
      '--payment-caption-size': theme.typography.pxToRem(12),
      '--payment-price-size': theme.typography.pxToRem(26),
      '--payment-label-size': theme.typography.pxToRem(14),
      '--payment-total-size': theme.typography.pxToRem(30),
      '--payment-fine-print-size': theme.typography.pxToRem(11.5),
      '--payment-control-size': theme.typography.pxToRem(12.5),
      '--payment-mobile-price-size': theme.typography.pxToRem(19),
      '--payment-mobile-total-size': theme.typography.pxToRem(24),
      '--payment-mobile-label-size': theme.typography.pxToRem(13),
      '--payment-weight-semibold': theme.typography.fontWeightSemiBold,
      '--payment-weight-bold': theme.typography.fontWeightBold,
      '--payment-weight-black': theme.typography.fontWeightBlack,
      '--payment-shadow-sm': theme.shadows[1],
      '--payment-footer-shadow': `0 -6px 20px ${alpha(theme.palette.secondary.dark, 0.1)}`,
    }}>
      <div className='payment-page__heading'>
        <h1><FormattedMessage id='payment.checkout.heading'/><span className='payment-page__dot' aria-hidden='true'/></h1>
        <p><FormattedMessage id='payment.checkout.subtitle'/></p>
      </div>

      <div className='payment-summary-mobile'>
        <button type='button' onClick={() => setSummaryOpen(open => !open)} aria-expanded={summaryOpen}>
          <span className='payment-summary-mobile__logo'>
            <img src={logos[offer.logo]} alt={offer.providerName || offer.productLineName}/>
          </span>
          <span className='payment-summary-mobile__toggle'>
            <FormattedMessage id={summaryOpen ? 'payment.checkout.closeDetails' : 'payment.checkout.orderSummary'}/>
          </span>
          <span className='payment-summary-mobile__price'><b>₪{offer.convertedPrice}</b><ChevronIcon open={summaryOpen}/></span>
        </button>
        {summaryOpen && <div className='payment-summary-mobile__body'><SummaryDetails offer={offer} initialTabs={initialTabs}/></div>}
      </div>

      <div className='payment-layout'>
        <Card variant='raised' padding='lg' className='payment-form-card' style={{borderRadius: 16}}>
          <div className='payment-fields'>
            <FormField label={intl.formatMessage({id: 'payment.fullname'})} htmlFor='ccOwnerName' error={fieldError('ccOwnerName')}>
              <Input
                id='ccOwnerName'
                name='ccOwnerName'
                size='lg'
                value={values.ccOwnerName}
                onChange={(event) => {
                  setFieldValue('ccOwnerName', event.target.value.replace(/[^ A-Za-z\u0590-\u05fe]/g, ''));
                }}
                placeholder={intl.formatMessage({id: 'payment.checkout.fullnamePlaceholder'})}
                invalid={Boolean(fieldError('ccOwnerName'))}
                iconStart={<PaymentFieldIcon name='user'/>}
                data-testid='payment-full-name'
              />
            </FormField>

            <FormField label={intl.formatMessage({id: 'payment.israeliId'})} htmlFor='ccOwnerId' error={fieldError('ccOwnerId')}>
              <Input
                id='ccOwnerId'
                name='ccOwnerId'
                size='lg'
                inputMode='numeric'
                maxLength={9}
                value={values.ccOwnerId}
                onChange={(event) => {
                  setFieldValue('ccOwnerId', event.target.value.replace(/\D/g, '').slice(0, 9));
                }}
                placeholder={intl.formatMessage({id: 'payment.checkout.idPlaceholder'})}
                invalid={Boolean(fieldError('ccOwnerId'))}
                iconStart={<PaymentFieldIcon name='id'/>}
                data-testid='payment-id'
              />
            </FormField>

            <FormField label={intl.formatMessage({id: 'payment.cc_number'})} htmlFor='ccNumber' error={fieldError('ccNumber')}>
              <Input
                id='ccNumber'
                name='ccNumber'
                size='lg'
                inputMode='numeric'
                autoComplete='cc-number'
                maxLength={CARD_NUMBER_MAX_LENGTH}
                value={values.ccNumber}
                onChange={(event) => setFieldValue(
                  'ccNumber',
                  event.target.value.replace(/\D/g, '').slice(0, CARD_NUMBER_MAX_LENGTH),
                )}
                placeholder={intl.formatMessage({id: 'payment.checkout.ccNumberPlaceholder'})}
                invalid={Boolean(fieldError('ccNumber'))}
                iconStart={<PaymentFieldIcon name='lock'/>}
                inputStyle={{direction: 'ltr', textAlign: 'left'}}
                data-testid='payment-credit-card-number'
              />
            </FormField>

            <FormField label={intl.formatMessage({id: 'payment.cc_type'})} htmlFor='ccType' error={fieldError('ccType')}>
              <div
                ref={cardTypeSelectRef}
                className={fieldError('ccType') ? 'payment-select payment-select--invalid' : 'payment-select'}
              >
                <button
                  ref={cardTypeTriggerRef}
                  type='button'
                  id='ccType'
                  className='payment-select__trigger'
                  role='combobox'
                  aria-controls={cardTypeOpen ? 'payment-card-type-options' : undefined}
                  aria-expanded={cardTypeOpen}
                  aria-haspopup='listbox'
                  aria-invalid={Boolean(fieldError('ccType'))}
                  onClick={() => setCardTypeOpen(open => !open)}
                  onKeyDown={event => {
                    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                      event.preventDefault();
                      setCardTypeOpen(true);
                    }
                  }}
                  data-testid='payment-select-card-type'
                >
                  <span>{selectedCardType?.name || intl.formatMessage({id: 'payment.checkout.selectCardType'})}</span>
                  <ChevronIcon open={cardTypeOpen}/>
                </button>
                {cardTypeOpen && (
                  <ul id='payment-card-type-options' className='payment-select__menu' role='listbox'>
                    {filteredCCTypes.map(item => (
                      <li key={item.id} role='option' aria-selected={String(item.id) === String(values.ccType)}>
                        <button
                          type='button'
                          onClick={() => selectCardType(item.id)}
                          data-testid={`payment-card-type-${item.id}`}
                        >
                          {item.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </FormField>

            <FormField label={intl.formatMessage({id: 'payment.checkout.expiration'})} htmlFor='ccExpiration' error={fieldError('ccExpiration')}>
              <Input
                id='ccExpiration'
                name='ccExpiration'
                size='lg'
                inputMode='numeric'
                autoComplete='cc-exp'
                value={values.ccExpiration}
                onChange={(event) => setFieldValue('ccExpiration', formatExpiryDate(event.target.value.slice(0, 5)))}
                placeholder='MM/YY'
                invalid={Boolean(fieldError('ccExpiration'))}
                inputStyle={{direction: 'ltr', textAlign: 'left'}}
                data-testid='payment-credit-card-expiration-date'
              />
            </FormField>

            <FormField label={intl.formatMessage({id: 'payment.checkout.cvv'})} htmlFor='cvv' error={fieldError('cvv')}>
              <div className='payment-cvv'>
              <Input
                id='cvv'
                name='cvv'
                size='lg'
                inputMode='numeric'
                autoComplete='cc-csc'
                value={values.cvv}
                onChange={(event) => setFieldValue('cvv', event.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder={intl.formatMessage({id: 'payment.checkout.cvvPlaceholder'})}
                invalid={Boolean(fieldError('cvv'))}
                inputStyle={{direction: 'ltr', textAlign: 'left'}}
                data-testid='payment-credit-card-cvv-number'
              />
              <Tooltip
                title={intl.formatMessage({id: 'payment.checkout.cvvTooltip'})}
                placement='top'
                enterTouchDelay={0}
                arrow
              >
                <button
                  type='button'
                  className='payment-cvv__help'
                  aria-label={intl.formatMessage({id: 'payment.checkout.cvvHelp'})}
                  data-testid='payment-cvv-help'
                >
                  <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor'
                    strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                    <circle cx='12' cy='12' r='9'/>
                    <path d='M12 11v5'/>
                    <path d='M12 8h.01'/>
                  </svg>
                </button>
              </Tooltip>
              </div>
            </FormField>
          </div>

          <div className='payment-notice' role='note'>
            <span aria-hidden='true'>!</span>
            <FormattedMessage id='payment.checkout.debitWarning'/>
          </div>
          {submitError && <p className='payment-submit-error' role='alert'>{submitError}</p>}
        </Card>

        <Card variant='raised' padding='lg' className='payment-summary-desktop' style={{borderRadius: 16}}>
          <SummaryDetails offer={offer} initialTabs={initialTabs}/>
          <Button variant='primary' size='lg' block loading={isSubmitting} onClick={onSubmit} data-testid='payment-continue'>
            <FormattedMessage id='continue'/>
          </Button>
          <p className='payment-secure'><FormattedMessage id='payment.checkout.secure'/></p>
          <button className='payment-back' type='button' onClick={onBackToOffers}>
            <FormattedMessage id='payment.checkout.backToOffers'/>
          </button>
        </Card>
      </div>

      <div className='payment-mobile-checkout'>
        <div><b>₪{offer.convertedPrice}</b><span>${Math.ceil(offer.price)} · <FormattedMessage id='payment.checkout.totalDue'/></span></div>
        <Button variant='primary' size='lg' block loading={isSubmitting} onClick={onSubmit} data-testid={TEST_IDS.payment.continueMobile}>
          <FormattedMessage id='continue'/>
        </Button>
      </div>
    </main>
  );
};

ChevronIcon.propTypes = {open: PropTypes.bool};
PaymentFieldIcon.propTypes = {name: PropTypes.oneOf(Object.keys(paymentFieldIconPaths)).isRequired};
SummaryDetails.propTypes = {initialTabs: PropTypes.object.isRequired, offer: PropTypes.object.isRequired};
PaymentCheckout.propTypes = {
  errors: PropTypes.object.isRequired,
  filteredCCTypes: PropTypes.array.isRequired,
  handleChange: PropTypes.func.isRequired,
  initialTabs: PropTypes.object.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  offer: PropTypes.object.isRequired,
  onBackToOffers: PropTypes.func.isRequired,
  onCardTypeChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  submitError: PropTypes.string,
  touched: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
};

export default PaymentCheckout;